/**
 * @file isv-应用管理-列表页
 * @author fightingliu
 * @created 2022.04.20
 */
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Icon } from '@tencent/spaui';
import { hooks, ListView, Loading, NoData } from '@tencent/ptp-fe-common';
import { getIsvAppList } from 'pages/manage/application/services';
import { showOpDialog } from 'pages/manage/application/util';
import {
  fetchAppCategoryOptions,
  fetchAppSecondCategoryOptions,
  fetchAppStatusOptions,
  fetchAuditOptions,
} from 'store/manager';
import { fetchAgencyDetail } from 'store/agency';
import { AgencyStatus } from 'model/agency';
import { useAppDispatch, useTypedSelector } from 'store/index';
import { report } from 'store/report';
import getTableConfig from './config/getTableConfig';
import { NOT_UPDATE_APP_TIPS } from 'pages/manage/application/constants';
// import getFilterConfig from './config/getFilterConfig';
import getToolbarConfig from './config/getToolbarConfig';
import '../index.scss';

interface ApplicationProps {
  payloads?: any;
  customFunctions?: any;
}
const options = ({ payloads, customFunctions }: ApplicationProps) => {
  const {
    listRef,
    currentRef,
    visibleColumns,
    hasOperationAuth,
    appStatusOptions,
    appCategoryOptions,
    appSecondCategoryOptions,
    auditOptions,
    notUpdateApp,
  } = payloads;

  const { loadList, hideDialog, showEditDialog, showDetailDialog, showExportDialog, setListInfo, setLoading } =
    customFunctions;

  return {
    fetchConfig: {
      action: getIsvAppList,
      $onRequest(params: any) {
        currentRef.current = { params };
        const retParams = params;
        // 排序
        if (params.order) {
          retParams.sort_type = params.order.type?.toLowerCase();
          retParams.sort_field = params.order.field;
        }
        return retParams;
      },
      $onResponse(data: any) {
        setLoading(false);
        // currentRef存储当前请求参数的引用
        const { params = {} } = currentRef.current;
        // const extra1 = {};
        _.map(_.keys(params), (key: string | number) => {
          const value = params[key];
          if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
            return;
          }
          // extra1[key] = value;
        });
        // extra1.action = 'ISV_APPLICATION_LIST_FILTER';
        // extra1.target = 'ISV-应用管理-列表-筛选结果';
        if (_.isEmpty(data)) return;
        // eventReport.savePageEvents(extra1);
        setListInfo(data);
        return { list: data };
      },
    },
    tableConfig: getTableConfig({
      listRef,
      currentRef,
      hasOperationAuth,
      loadList,
      visibleColumns,
      hideDialog,
      showEditDialog,
      showDetailDialog,
      showExportDialog,
      appStatusOptions,
      appCategoryOptions,
      appSecondCategoryOptions,
      auditOptions,
    }),
    // filterConfig: getFilterConfig({
    //   appStatusOptions,
    //   appCategoryOptions,
    //   appSecondCategoryOptions,
    //   showEditDialog,
    //   showDetailDialog,
    //   showExportDialog,
    // }),
    toolbarConfig: getToolbarConfig({
      showEditDialog,
      showDetailDialog,
      showExportDialog,
      notUpdateApp,
    }),
  };
};
const { useDialog } = hooks;
const ApplicationList: () => JSX.Element = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { Dialog, setDialogOptions, hideDialog } = useDialog();
  const [listInfo, setListInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const listRef = useRef(null);
  const currentRef = useRef(null);

  const {
    manager: { appStatusOptions, appCategoryOptions, appSecondCategoryOptions, auditOptions },
    agency: { agencyDetail },
  } = useTypedSelector((state) => state);

  // 是否可以添加应用
  const notUpdateApp = ![AgencyStatus.pass, AgencyStatus.pause].includes(agencyDetail?.status);

  // 获取通用配置项目
  const getCommonOptions = useCallback(() => {
    dispatch(fetchAppStatusOptions());
    dispatch(fetchAppCategoryOptions());
    dispatch(fetchAppSecondCategoryOptions());
    dispatch(fetchAuditOptions());
    dispatch(fetchAgencyDetail());
  }, [dispatch]);

  useEffect(() => {
    report({ event: 'application_manage', payload: '应用列表界面' });
    getCommonOptions();
  }, [getCommonOptions]);

  // 添加应用
  const showEditDialog = () => {
    history.push('/manage/application/operation/create');
  };

  // 点击跳转到详情界面
  const showDetailDialog = (projectId: any) => {
    history.push(`/manage/application/operation/edit/${projectId}`);
  };

  // 导出报表弹窗
  const showExportDialog = (id: string, title: string) => {
    const notify = '导出成功';
    const payload = {
      id,
      type: 'export',
      appStatusOptions,
      appCategoryOptions,
      appSecondCategoryOptions,
      auditOptions,
    };
    showOpDialog(`跨账户报表导出 (${title})`, '提交', notify, payload, listRef, { setDialogOptions, hideDialog });
  };

  const { loadList } = listRef.current || {};
  const customFunctions = {
    setDialogOptions,
    loadList,
    hideDialog,
    showEditDialog,
    showDetailDialog,
    showExportDialog,
    setListInfo,
    setLoading,
  };
  const payloads = {
    listRef,
    currentRef,
    hideDialog,
    appStatusOptions,
    appCategoryOptions,
    appSecondCategoryOptions,
    auditOptions,
    notUpdateApp,
  };

  const emptyView = loading ? (
    <Loading />
  ) : (
    <div className="app-empty">
      <NoData className="app-nodata" content={'暂无应用，点击下方按钮添加'} />
      <Button
        className="tab-button"
        onClick={() => showEditDialog()}
        displayType="primary"
        disabled={notUpdateApp}
        tip={notUpdateApp && NOT_UPDATE_APP_TIPS}
      >
        <div className="icon-plus">
          <Icon name="plus-outlined" style={{ marginRight: 6 }} />
          添加应用
        </div>
      </Button>
    </div>
  );

  const loadDataList = useMemo(() => {
    return (
      <ListView
        className="crm-isv-lists"
        ref={listRef}
        // @ts-ignore
        options={options}
        payloads={payloads}
        customFunctions={customFunctions}
      />
    );
  }, [customFunctions, payloads]);

  return (
    <div className="page-form-group">
      <div style={{ display: 'none' }}>{loadDataList}</div>
      <div className="form-group-header">
        <h4>全部应用</h4>
      </div>
      <div className="form-group-content" style={{ padding: 0 }}>
        {_.isEmpty(listInfo) ? emptyView : loadDataList}
      </div>
      {Dialog}
    </div>
  );
};
export default ApplicationList;
